/** /onboarding/google-scope-picker - after Google OAuth created
 *  several connector instances with pending_scope_picker=1, the user
 *  assigns a primary + sub scope to each before ingestion runs. No
 *  service can proceed until its primary is set. No em dashes. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Calendar,
  FolderOpen,
  Image,
  Contact,
  Plug,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import {
  loadScopeDefinitions,
  type Primary,
  type ScopeDefinitions,
  FALLBACK_SCOPE_DEFS,
} from "../lib/scope-meta";
import { getCsrfToken } from "../lib/auth";
import { ScopeOption } from "../components/ScopeOption";

interface Pending {
  id: string;
  kind: string;
  account: string;
}

function iconFor(kind: string): LucideIcon {
  const k = kind.toLowerCase();
  if (k.includes("gmail") || k.includes("mail")) return Mail;
  if (k.includes("calendar")) return Calendar;
  if (k.includes("drive")) return FolderOpen;
  if (k.includes("photo")) return Image;
  if (k.includes("contact")) return Contact;
  return Plug;
}

export function GoogleScopePicker({
  embedded = false,
  redirectTo = "/today",
}: {
  /** Rendered inside the connectors sub-page (no full-screen
   *  wrapper, no auto-redirect away when empty). */
  embedded?: boolean;
  /** Where Save lands. The standalone onboarding flow goes to
   *  /today; the in-page Configure step returns to the connected
   *  list. */
  redirectTo?: string;
}) {
  const nav = useNavigate();
  const [pending, setPending] = useState<Pending[] | null>(null);
  const [defs, setDefs] = useState<ScopeDefinitions>(FALLBACK_SCOPE_DEFS);
  const [sel, setSel] = useState<
    Record<string, { primary: Primary | ""; sub: string | null }>
  >({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void loadScopeDefinitions().then(setDefs);
    fetch("/api/connectors/pending-scope", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((d) => setPending(Array.isArray(d.pending) ? d.pending : []))
      .catch(() => setPending([]));
  }, []);

  // No pending instances: the standalone onboarding flow continues
  // to /today; the embedded Configure step just shows an empty
  // state (it lives inside the connectors sub-page already).
  useEffect(() => {
    if (!embedded && pending && pending.length === 0)
      nav("/today", { replace: true });
  }, [embedded, pending, nav]);

  const allSet = useMemo(
    () =>
      !!pending &&
      pending.length > 0 &&
      pending.every((p) => sel[p.id]?.primary),
    [pending, sel],
  );

  const saveAll = useCallback(async () => {
    if (!pending || !allSet) return;
    setBusy(true);
    setErr(null);
    const t = getCsrfToken();
    try {
      for (const p of pending) {
        const choice = sel[p.id];
        const res = await fetch(
          `/api/connectors/${encodeURIComponent(p.id)}/assign-scope`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(t ? { "X-CSRF-Token": t } : {}),
            },
            credentials: "same-origin",
            body: JSON.stringify({
              primary_scope: choice.primary,
              sub_scope:
                choice.primary === "private" ? null : choice.sub,
            }),
          },
        );
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Failed for ${p.kind}`);
        }
      }
      nav(redirectTo, { replace: true });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [pending, sel, allSet, nav, redirectTo]);

  if (!pending) {
    return (
      <div
        className={`flex items-center justify-center text-slate-400 ${
          embedded ? "py-16" : "min-h-screen"
        }`}
      >
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (embedded && pending.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-sm text-slate-500 dark:text-slate-400">
        No Google services are waiting for scope. Connect one from
        Browse to set it up.
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? "space-y-5"
          : "min-h-screen max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5"
      }
    >
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          Set scope for each connected Google service
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Pick where each service's data lands. Nothing is captured
          until every service has a scope.
        </p>
      </div>

      <div className="space-y-3">
        {pending.map((p) => {
          const Icon = iconFor(p.kind);
          const choice = sel[p.id] ?? { primary: "", sub: null };
          const subs =
            choice.primary === "personal"
              ? defs.personal
              : choice.primary === "work"
                ? defs.work
                : [];
          return (
            <div
              key={p.id}
              className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-3.5 space-y-3"
            >
              <div className="flex items-center gap-2.5">
                <span className="rounded-lg bg-slate-100 dark:bg-slate-800 p-2">
                  <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                </span>
                <div className="font-semibold text-sm capitalize">
                  {p.kind.replace(/[-_]/g, " ")}
                  <span className="text-slate-400 font-normal">
                    {" "}
                    · {p.account}
                  </span>
                </div>
              </div>
              <div
                role="radiogroup"
                aria-label={`Scope for ${p.kind}`}
                className="flex gap-1.5"
              >
                {(["personal", "work", "private"] as const).map((pp) => {
                  const disabled = pp === "private";
                  return (
                    <ScopeOption
                      key={pp}
                      compact
                      selected={choice.primary === pp}
                      disabled={disabled}
                      title={
                        disabled
                          ? "Auto-detected from content, not selectable here"
                          : undefined
                      }
                      onSelect={() =>
                        setSel((s) => ({
                          ...s,
                          [p.id]: { primary: pp, sub: null },
                        }))
                      }
                      label={pp}
                      className="flex-1"
                    />
                  );
                })}
              </div>
              {subs.length > 0 && (
                <select
                  value={choice.sub ?? ""}
                  onChange={(e) =>
                    setSel((s) => ({
                      ...s,
                      [p.id]: {
                        primary: choice.primary,
                        sub: e.target.value || null,
                      },
                    }))
                  }
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1.5 text-xs"
                >
                  <option value="">
                    {choice.primary === "personal" ? "General" : "Default"}
                  </option>
                  {subs.map((x) => (
                    <option key={x} value={x}>
                      {x.charAt(0).toUpperCase() + x.slice(1)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>

      {err && (
        <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
      )}
      <button
        type="button"
        disabled={!allSet || busy}
        onClick={saveAll}
        className="w-full rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-40"
      >
        {busy ? "Saving..." : "Save all and continue"}
      </button>
      {!allSet && (
        <p className="text-xs text-slate-400 text-center">
          Pick a primary scope for every service to continue.
        </p>
      )}
    </div>
  );
}

export default GoogleScopePicker;
